import React, { useEffect, useState } from 'react';
import BottomSheet from '../components/BottomSheet';

const FIELDS = [
  { key: 'nom', label: 'Nom du restaurant', type: 'text' },
  { key: 'ville', label: 'Ville', type: 'text' },
  // Coordonnées de l'établissement — éditables sur le web, elles manquaient ici.
  { key: 'adresse', label: 'Adresse', type: 'text' },
  { key: 'telephone', label: 'Téléphone', type: 'text' },
  { key: 'site_web', label: 'Site web', type: 'url' },
  { key: 'google_place_id', label: 'Google Place ID', type: 'text', helper: 'Format ChIJ… ou similaire (récupéré dans Google Maps)' },
  { key: 'google_maps_url', label: 'Lien Google Maps', type: 'url' },
  { key: 'ticket_moyen', label: 'Ticket moyen (€)', type: 'number' },
  { key: 'couverts_semaine', label: 'Couverts par semaine', type: 'number' },
  { key: 'clients_par_jour', label: 'Clients par jour', type: 'number' },
  { key: 'note_depart', label: 'Note Google de départ', type: 'number', step: '0.1' },
  { key: 'avis_depart', label: 'Nb avis Google de départ', type: 'number' },
  { key: 'delai_envoi_minutes', label: "Délai d'envoi SMS (minutes)", type: 'number' },
  { key: 'objectif_avis', label: "Objectif d'avis (30j)", type: 'number' },
  { key: 'objectif_horizon_mois', label: 'Horizon de l\'objectif (mois)', type: 'number', helper: 'Entre 1 et 36 mois' },
];

export default function EditRestaurantSheet({ open, restaurant, onClose, onSave }) {
  const [values, setValues] = useState({});
  const [whatsapp, setWhatsapp] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!open) return;
    const init = {};
    FIELDS.forEach((f) => { init[f.key] = restaurant?.[f.key] ?? ''; });
    setValues(init);
    setWhatsapp(restaurant?.whatsapp_message || '');
    setToast(null);
  }, [open, restaurant]);

  const close = () => { if (!saving) onClose && onClose(); };

  const setField = (key, v) => setValues(prev => ({ ...prev, [key]: v }));

  const handleSave = async () => {
    setSaving(true);
    setToast(null);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        const v = values[f.key];
        if (v === '' || v == null) return;
        if (f.type === 'number') {
          const n = Number(v);
          if (!Number.isNaN(n)) payload[f.key] = n;
        } else {
          payload[f.key] = String(v);
        }
      });
      if (whatsapp != null && whatsapp !== restaurant?.whatsapp_message) {
        payload.whatsapp_message = whatsapp;
      }
      await onSave(payload);
      setToast('✅ Restaurant mis à jour');
      setTimeout(() => { close(); }, 1000);
    } catch (e) {
      setToast('❌ ' + (e.message || 'Erreur de mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={close} title="Configurer le restaurant" height="full">
      <p style={styles.intro}>
        Les modifications sont enregistrées immédiatement. Modifier le Place ID Google
        re-déclenche la synchronisation des avis (Outscraper).
      </p>

      <div style={styles.grid}>
        {FIELDS.map((f) => (
          <div key={f.key} style={styles.field}>
            <label style={styles.label}>{f.label}</label>
            <input
              type={f.type}
              step={f.step}
              value={values[f.key] ?? ''}
              onChange={(e) => setField(f.key, e.target.value)}
              disabled={saving}
              style={styles.input}
            />
            {f.helper && <span style={styles.helper}>{f.helper}</span>}
          </div>
        ))}

        <div style={styles.field}>
          <label style={styles.label}>Message WhatsApp / SMS</label>
          <textarea
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            disabled={saving}
            rows={4}
            style={{ ...styles.input, resize: 'vertical', lineHeight: 1.5 }}
            placeholder="Bonjour {prenom}, merci pour votre visite ! Votre avis nous aide beaucoup."
          />
          <span style={styles.helper}>Variables disponibles : {'{'}prenom{'}'}, {'{'}nom_restaurant{'}'}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        style={{ ...styles.cta, opacity: saving ? 0.7 : 1 }}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>

      {toast && <div style={styles.toast}>{toast}</div>}
    </BottomSheet>
  );
}

const styles = {
  intro: {
    margin: '0 0 16px',
    fontSize: 13,
    color: '#6B6B6B',
    lineHeight: 1.5,
  },
  grid: { display: 'flex', flexDirection: 'column', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#6B6B6B' },
  input: {
    border: '1px solid #E5E5E0',
    background: '#FFFFFF',
    borderRadius: 10,
    padding: '11px 14px',
    fontSize: 15,
    fontFamily: 'inherit',
    color: '#1A1A1A',
    outline: 'none',
    boxSizing: 'border-box',
  },
  helper: { color: '#9A9A9A', fontSize: 11 },
  cta: {
    marginTop: 16,
    width: '100%',
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  toast: {
    marginTop: 12,
    padding: '10px 14px',
    background: '#E8F5EE',
    color: '#2D7A4A',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 10,
    textAlign: 'center',
  },
};
